import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid, Typography, Box, SxProps, Theme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getCompetitionRowKey, selectFieldStyles } from 'quoting/new-quote/helpers';
import { CompetitionTable } from 'quoting/new-quote/tables/competition-table';
import { Competition } from 'shared/types';
import { FormTextField, SelectField } from 'shared/controls';
import { competitionInfoSourceOptions } from 'shared/constants';

const alertStyles: SxProps<Theme> = {
  border: '1px solid #0288d1',
  borderRadius: '5px',
  color: '#0288d1',
  backgroundColor: '#e8f4fd',
  padding: 1,
  marginBottom: 1,
};

export const CompetitionInfo = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();

  const competitionItems = getValues('competitionInfoCompetition') as Competition[];
  const [rows, setRows] = useState<Competition[]>(competitionItems ?? []);

  useEffect(() => {
    setValue('competitionInfoCompetition', rows);
  }, [rows, setValue]);

  const handleAddRow = (newRow: Competition) => {
    setRows(prevState => {
      const existingRow = prevState.find(
        currentRow => getCompetitionRowKey(currentRow) === getCompetitionRowKey(newRow)
      );
      if (!existingRow) {
        return [...prevState, newRow];
      }

      enqueueSnackbar(`This competition is already listed. `, { variant: 'warning' });
      return [...prevState];
    });
  };

  const handleRemoveRow = (row: Competition) =>
    setRows(prevState => {
      const existingRowIndex = prevState.findIndex(
        currentRow => getCompetitionRowKey(currentRow) === getCompetitionRowKey(row)
      );
      if (existingRowIndex < 0) {
        enqueueSnackbar(`Could not remove competition line.`, { variant: 'error' });
        return prevState;
      }
      const newRows = [...prevState];
      newRows.splice(existingRowIndex, 1);
      return [...newRows];
    });

  const sourceIsOther = watch('competitionInfoSourceOfInfo') === 'OTHER';

  return (
    <Grid item xs={12} md={6}>
      <Typography variant='h6'>Competition</Typography>
      <Typography sx={alertStyles} variant='body2'>
        Click add button to save competitive information before moving to next section.
      </Typography>
      <CompetitionTable rows={rows} handleAddRow={handleAddRow} handleRemoveRow={handleRemoveRow} />
      <Box sx={{ marginTop: 2 }}>
        <SelectField
          name='competitionInfoSourceOfInfo'
          label='Source of Competitive Information'
          options={[...competitionInfoSourceOptions]}
          getOptionLabel={opt => opt}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return true;
            return option === value;
          }}
          sx={selectFieldStyles}
        />
      </Box>
      {sourceIsOther && (
        <Box sx={{ marginTop: 1 }}>
          <FormTextField
            name='competitionInfoOtherSourceOfInfo'
            label='Other Source of Competitive Information'
            rules={{
              validate: (value: string) =>
                getValues('status') === 'DRAFT' || !!value || 'Competition Source of Info is required',
            }}
            error={Boolean(errors?.competitionInfoOtherSourceOfInfo)}
            helperText={errors?.competitionInfoOtherSourceOfInfo?.message}
          />
        </Box>
      )}
    </Grid>
  );
};
